import { Dispatch } from 'react';
import { TOGGLE_MODAL } from 'state/actionTypes/modalActionTypes';
import { MyDeal } from 'state/contexts/DealsContext/DealsContext.types';
import {
  AuthModalOptions,
  ModalAction,
  ModalData,
  ModalState,
  ModalType,
} from 'state/contexts/modalContext/types';
import { UpdatingStorageProviderState } from 'state/contexts/storageProviderContext/types';
import { ApiStorageProvider } from 'types/storageProvider';

type ToggleModal = (vals: {
  data: ModalData;
  action: boolean;
  dispatch: Dispatch<ModalAction>;
}) => void;
export const toggleModal: ToggleModal = ({ data, action, dispatch }) => {
  dispatch({
    type: TOGGLE_MODAL,
    payload: { data, action },
  });
};

/**
 * Handles the state changes for closing
 * the modal that is currently open
 *
 * @param dispatch {Function}
 */
type CloseModal = (dispatch: Dispatch<ModalAction>) => void;
export const closeModal: CloseModal = (dispatch) => {
  dispatch({
    type: TOGGLE_MODAL,
    payload: {
      data: {
        modalType: ModalType.NO_MODAL,
        modalProps: {} as ModalState['modalProps'],
      },
      action: false,
    },
  });
};
type OpenModalEditProfile = (
  storageProvider: ApiStorageProvider,
  dispatch: Dispatch<ModalAction>,
) => void;
export const openModalEditProfile: OpenModalEditProfile = async (
  storageProvider,
  dispatch,
) => {
  const closeModalFunc = () => closeModal(dispatch);

  const modalProps = {
    storageProvider,
    closeModal: closeModalFunc,
    width: 620,
    className: 'modal-edit-profile',
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_EDIT_PROFILE, modalProps },
    action: true,
    dispatch,
  });
};

export type OpenModalClientAuthenticate = (
  dispatch: Dispatch<ModalAction>,
  options?: AuthModalOptions,
) => void;
export const openModalClientAuthenticate: OpenModalClientAuthenticate = (
  dispatch,
  options = {},
) => {
  const closeModalFunc = () => closeModal(dispatch);

  const modalProps = {
    closeModal: closeModalFunc,
    width: 620,
    options,
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_AUTHENTICATE_CLIENT, modalProps },
    action: true,
    dispatch,
  });
};

export type OpenModalAuthenticate = (
  dispatch: Dispatch<ModalAction>,
  shouldAddAddress: boolean,
  options?: AuthModalOptions,
) => void;
export const openModalAuthenticate: OpenModalAuthenticate = (
  dispatch,
  shouldAddAddress = false,
  options = {},
) => {
  const closeModalFunc = () => closeModal(dispatch);

  const modalProps = {
    closeModal: closeModalFunc,
    width: 620,
    shouldAddAddress,
    options,
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_AUTHENTICATE, modalProps },
    action: true,
    dispatch,
  });
};

export const openModalAuthenticateLedger: OpenModalAuthenticate = (
  dispatch,
  shouldAddAddress = false,
  options = {},
) => {
  const closeModalFunc = () => closeModal(dispatch);

  const modalProps = {
    closeModal: closeModalFunc,
    width: 620,
    shouldAddAddress,
    options,
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_AUTHENTICATE_LEDGER, modalProps },
    action: true,
    dispatch,
  });
};

export const openModalAuthenticateSignature: OpenModalAuthenticate = (
  dispatch,
  shouldAddAddress = false,
  options = {},
) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    closeModal: closeModalFunc,
    width: 620,
    shouldAddAddress,
    options,
    className: 'modal-auth-signature',
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_AUTHENTICATE_SIGNATURE, modalProps },
    action: true,
    dispatch,
  });
};

type OpenModalAllowance = (
  dispatch: Dispatch<ModalAction>,
  minerId: string,
  ownerAddress: string,
) => void;
export const openModalAllowance: OpenModalAllowance = (
  dispatch,
  minerId,
  ownerAddress,
) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    closeModal: closeModalFunc,
    width: 980,
    minerId,
    ownerAddress,
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_ALLOWANCE, modalProps },
    action: true,
    dispatch,
  });
};

export type OpenModalMyDealLeaveReview = (
  dispatch: Dispatch<ModalAction>,
  myDeal: MyDeal,
  rating: number,
  setRating: (params: number) => void,
) => void;
export const openModalMyDealLeaveReview: OpenModalMyDealLeaveReview = (
  dispatch,
  myDeal,
  rating,
  setRating,
) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    closeModal: closeModalFunc,
    width: 620,
    myDeal,
    rating,
    setRating,
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_MY_DEAL_REVIEW_MESSAGE, modalProps },
    action: true,
    dispatch,
  });
};

export type OpenModalEarlyAccess = (dispatch: Dispatch<ModalAction>) => void;
export const openModalEarlyAccess: OpenModalEarlyAccess = (dispatch) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    closeModal: closeModalFunc,
    width: 5000,
    className: 'modal-pink',
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_EARLY_ACCESS, modalProps },
    action: true,
    dispatch,
  });
};

export type OpenModalFilstationEarlyAccess = (dispatch: Dispatch<ModalAction>) => void;
export const openModalFilstationEarlyAccess: OpenModalFilstationEarlyAccess = (
  dispatch,
) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    closeModal: closeModalFunc,
    width: 5000,
    className: 'modal-pink',
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_FILSTATION_EARLY_ACCESS, modalProps },
    action: true,
    dispatch,
  });
};

export type OpenModalFilesUpload = (dispatch: Dispatch<ModalAction>) => void;
export const openModalFilesUpload: OpenModalFilesUpload = (dispatch) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    closeModal: closeModalFunc,
    width: 1000,
    className: 'modal-files-upload',
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_FILES_UPLOAD, modalProps },
    action: true,
    dispatch,
  });
};

export type OpenModalConfirmDeleteFiles = (
  dispatch: Dispatch<ModalAction>,
  selectedFiles: string[],
) => void;
export const openModalConfirmDeleteFiles: OpenModalConfirmDeleteFiles = (
  dispatch,
  selectedFiles,
) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    closeModal: closeModalFunc,
    width: 610,
    selectedFiles,
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_CONFIRM_FILE_DELETE, modalProps },
    action: true,
    dispatch,
  });
};

export type OpenModalEditEmail = (dispatch: Dispatch<ModalAction>) => void;
export const openModalEditEmail: OpenModalEditEmail = (dispatch) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    closeModal: closeModalFunc,
    width: 610,
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_EDIT_EMAIL, modalProps },
    action: true,
    dispatch,
  });
};

export type OpenModalStorageProviderEditPreview = (
  dispatch: Dispatch<ModalAction>,
  changedValues: UpdatingStorageProviderState,
) => void;
export const openModalStorageProviderEditPreview: OpenModalStorageProviderEditPreview = (
  dispatch,
  changedValues,
) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    changedValues,
    closeModal: closeModalFunc,
    width: 1360,
    className: 'modal-sp-edit-preview',
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_STORAGE_PROVIDER_EDIT_PREVIEW, modalProps },
    action: true,
    dispatch,
  });
};

export type openModalChangeEmailMessage = (
  dispatch: Dispatch<ModalAction>,
  message: string,
) => void;
export const openModalChangeEmailMessage: openModalChangeEmailMessage = (
  dispatch,
  message,
) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    closeModal: closeModalFunc,
    width: 610,
    message,
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_SP_HAS_EMAIL_POPUP, modalProps },
    action: true,
    dispatch,
  });
};

export type OpenModalStorageRequest = (dispatch: Dispatch<ModalAction>) => void;
export const openModalStorageRequest: OpenModalStorageRequest = (dispatch) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    closeModal: closeModalFunc,
    width: 1300,
    className: 'modal-storage-request-wizard',
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_STORAGE_REQUEST, modalProps },
    action: true,
    dispatch,
  });
};

export type OpenModalCheckEmailMessage = (
  dispatch: Dispatch<ModalAction>,
  email: string,
) => void;
export const openModalCheckEmailMessage: OpenModalCheckEmailMessage = (
  dispatch,
  email,
) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    closeModal: closeModalFunc,
    width: 610,
    email,
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_CHECK_EMAIL_MESSAGE, modalProps },
    action: true,
    dispatch,
  });
};

export type OpenModalInitialFilecoinAddressConnect = (
  dispatch: Dispatch<ModalAction>,
) => void;
export const openModalInitialFilecoinAddressConnect: OpenModalInitialFilecoinAddressConnect =
  (dispatch) => {
    const closeModalFunc = () => {
      closeModal(dispatch);
    };

    const modalProps = {
      closeModal: closeModalFunc,
      width: 610,
    };

    toggleModal({
      data: { modalType: ModalType.MODAL_INITIAL_FILECOIN_ADDRESS_CONNECT, modalProps },
      action: true,
      dispatch,
    });
  };

export type OpenModalConnectAddress = (dispatch: Dispatch<ModalAction>) => void;
export const openModalConnectAddress: OpenModalConnectAddress = (dispatch) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    closeModal: closeModalFunc,
    width: 800,
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_CONNECTED_ADDRESSES, modalProps },
    action: true,
    dispatch,
  });
};

export type OpenModalAccountSettings = (dispatch: Dispatch<ModalAction>) => void;
export const openModalAccountSettings: OpenModalAccountSettings = (dispatch) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    closeModal: closeModalFunc,
    width: 690,
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_ACCOUNT_SETTINGS, modalProps },
    action: true,
    dispatch,
  });
};

export type OpenModalRejectStorageRequest = (
  dispatch: Dispatch<ModalAction>,
  storageRequestId: string,
) => void;
export const openModalRejectStorageRequest: OpenModalRejectStorageRequest = (
  dispatch,
  storageRequestId,
) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    closeModal: closeModalFunc,
    width: 540,
    storageRequestId,
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_REJECT_STORAGE_REQUEST, modalProps },
    action: true,
    dispatch,
  });
};

export type OpenModalLoginRequired = (dispatch: Dispatch<ModalAction>) => void;
export const openModalLoginRequired: OpenModalLoginRequired = (dispatch) => {
  const closeModalFunc = () => {
    closeModal(dispatch);
  };

  const modalProps = {
    closeModal: closeModalFunc,
    width: 690,
  };

  toggleModal({
    data: { modalType: ModalType.MODAL_LOGIN_REQUIRED, modalProps },
    action: true,
    dispatch,
  });
};
